/**
 * Hook para buscar mensagem de reply on-demand
 *
 * MOTIVAÇÃO:
 * - Otimização de performance: reduz lookups no MongoDB de 7 para 2 por mensagem
 * - Backend não popula mais reply/forward nos endpoints de listagem
 * - Frontend busca reply apenas quando necessário (usuário vê mensagem com replyToMessageId)
 *
 * USO:
 * const { reply, isLoading, error } = useReplyMessage(message.replyToMessageId);
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ConversationMessageResponse } from '@troia-v3/types';

interface UseReplyMessageOptions {
  enabled?: boolean;
  staleTime?: number;
}

export const useReplyMessage = (
  replyToMessageId?: string,
  options?: UseReplyMessageOptions
) => {
  return useQuery<ConversationMessageResponse>({
    queryKey: ['conversation-message', replyToMessageId],
    queryFn: async () => {
      if (!replyToMessageId) {
        throw new Error('replyToMessageId is required');
      }

      // GET /conversation-messages/:id (baseURL já contém /api)
      // Este endpoint usa getById() que popula reply/forward
      const response = await api.get(`/conversation-messages/${replyToMessageId}`);

      // Backend retorna { success: true, data: {...} }
      return response.data.data;
    },
    enabled: !!replyToMessageId && (options?.enabled !== false),
    staleTime: options?.staleTime ?? 10 * 60 * 1000, // 10 minutos (cache agressivo)
    retry: 2,
    refetchOnWindowFocus: false,
  });
};
